import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { User } from '../../types';
import { getGraphQLClient, SIGN_IN, GOOGLE_SIGN_IN } from '../../lib/graphql';
import { captureException } from '../../lib/sentry';
import { Button, Input } from '../common';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (
            element: HTMLElement,
            config: { theme: string; size: string; width: number }
          ) => void;
        };
      };
    };
  }
}

export default function LoginForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const client = getGraphQLClient();
      console.log('Making login request...');
      const data = await client.request<{
        signIn: {
          token: string;
          user: User;
          errors: string[];
        };
      }>(SIGN_IN, { email, password });

      console.log('Login response:', data);

      if (data.signIn.errors.length > 0) {
        setError(data.signIn.errors[0]);
      } else {
        console.log('Calling login() with token:', data.signIn.token);
        login(data.signIn.token, data.signIn.user);
        console.log('Navigating to /');
        navigate('/');
      }
    } catch (error) {
      captureException(error, {
        component: 'LoginForm',
        action: 'signIn',
        email,
      });
      setError(t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async (credential: string) => {
    setIsLoading(true);
    setError('');

    try {
      const client = getGraphQLClient();
      const data = await client.request<{
        googleSignIn: {
          token: string;
          user: User;
          errors: string[];
        };
      }>(GOOGLE_SIGN_IN, { idToken: credential });

      if (data.googleSignIn.errors.length > 0) {
        setError(data.googleSignIn.errors[0]);
      } else {
        login(data.googleSignIn.token, data.googleSignIn.user);
        navigate('/');
      }
    } catch (error) {
      captureException(error, {
        component: 'LoginForm',
        action: 'googleSignIn',
      });
      setError(t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initializeGoogleSignIn = () => {
      if (window.google) {
        const googleClientId =
          document.querySelector('meta[name="google-client-id"]')?.getAttribute('content') || '';
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: (response) => handleGoogleLogin(response.credential),
        });

        const buttonDiv = document.getElementById('google-signin-button');
        if (buttonDiv) {
          window.google.accounts.id.renderButton(buttonDiv, {
            theme: 'outline',
            size: 'large',
            width: 300,
          });
        }
      }
    };

    // If Google script is already loaded, initialize immediately
    if (window.google) {
      initializeGoogleSignIn();
    } else {
      // Otherwise, wait for the script to load
      const checkGoogle = setInterval(() => {
        if (window.google) {
          clearInterval(checkGoogle);
          initializeGoogleSignIn();
        }
      }, 100);

      // Clean up interval on unmount
      return () => clearInterval(checkGoogle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md"
    >
      <div className="card p-8">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold text-gradient mb-2 hover:opacity-80 transition-opacity">
              {t('app.name')}
            </h1>
          </Link>
          <p className="text-gray-600 dark:text-gray-400">{t('app.tagline')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="email"
            label={t('auth.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />

          <Input
            type="password"
            label={t('auth.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
          />

          <div className="text-right">
            <Link
              to="/forgot-password"
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              {t('auth.forgotPassword')}
            </Link>
          </div>

          {error && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-sm text-red-500 text-center"
            >
              {error}
            </motion.p>
          )}

          <Button type="submit" className="w-full" isLoading={isLoading}>
            {t('auth.login')}
          </Button>
        </form>

        <div className="my-6 flex items-center">
          <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
          <span className="px-4 text-sm text-gray-500">{t('auth.or')}</span>
          <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
        </div>

        <div id="google-signin-button" className="flex justify-center" />

        <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          {t('auth.noAccount')}{' '}
          <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium">
            {t('auth.register')}
          </Link>
        </p>
      </div>
    </motion.div>
  );
}
